import React, { useState, useEffect, useCallback } from 'react';
import { driveAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  Loader2,
  Search,
  FolderOpen,
  FileText,
  ChevronRight,
  Home,
  ArrowLeft,
  Check,
  HardDrive,
} from 'lucide-react';

const DriveBrowser = ({ 
  open, 
  onOpenChange, 
  onSelect, 
  mode = 'template', // 'template' or 'folder'
  title,
  description 
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [error, setError] = useState(null);
  
  // Shared Drives state
  const [sharedDrives, setSharedDrives] = useState([]);
  const [selectedDriveId, setSelectedDriveId] = useState(null);
  const [needsDriveSelection, setNeedsDriveSelection] = useState(true);
  
  const fetchItems = useCallback(async (folderId = null, query = '', driveId = null) => {
    setLoading(true);
    setError(null);
    console.log('DriveBrowser fetchItems:', { folderId, query, driveId });
    try {
      const response = await driveAPI.browse(folderId, query, driveId);
      console.log('DriveBrowser response:', response.data);
      
      // Check if we need to select a shared drive first
      if (response.data.needs_drive_selection) {
        setSharedDrives(response.data.shared_drives || []);
        setNeedsDriveSelection(true);
        setItems([]);
        return;
      }
      
      setNeedsDriveSelection(false);
      console.log('Setting items:', response.data.items);
      setItems(response.data.items || []);
      setCurrentFolder(response.data.current_folder);
    } catch (err) {
      console.error('DriveBrowser error:', err);
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('non connesso')) {
        setError('drive_not_connected');
        toast.error('Google Drive non connesso. Vai nelle Impostazioni per connetterlo.');
      } else {
        setError('generic');
        toast.error('Errore nel caricamento');
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setCurrentFolderId(null);
      setCurrentFolder(null);
      setBreadcrumbs([]);
      setSelectedItem(null);
      setError(null);
      setSelectedDriveId(null);
      setNeedsDriveSelection(true);
      fetchItems(null, '', null);
    }
  }, [open, fetchItems]);

  const handleSearch = () => {
    fetchItems(currentFolderId, searchQuery, selectedDriveId);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectSharedDrive = (drive) => {
    setSelectedDriveId(drive.id);
    setBreadcrumbs([{ id: drive.id, name: drive.name, isDrive: true }]);
    setNeedsDriveSelection(false);
    fetchItems(null, '', drive.id);
  };

  const handleFolderClick = (folder) => {
    setCurrentFolderId(folder.id);
    setBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name }]);
    setSearchQuery('');
    fetchItems(folder.id, '', selectedDriveId);
  };

  const handleBreadcrumbClick = (index) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    const targetFolder = newBreadcrumbs[newBreadcrumbs.length - 1];
    
    // If clicking on the drive root
    if (targetFolder.isDrive) {
      setCurrentFolderId(null);
      fetchItems(null, '', targetFolder.id);
    } else {
      setCurrentFolderId(targetFolder.id);
      fetchItems(targetFolder.id, '', selectedDriveId);
    }
    setSearchQuery('');
  };

  const handleGoBack = () => {
    if (breadcrumbs.length > 1) {
      handleBreadcrumbClick(breadcrumbs.length - 2);
    } else if (breadcrumbs.length === 1) {
      // Go back to drive selection
      setNeedsDriveSelection(true);
      setSelectedDriveId(null);
      setBreadcrumbs([]);
      setItems([]);
      fetchItems(null, '', null);
    }
  };

  const handleItemSelect = (item) => {
    if (mode === 'template' && item.type === 'document') {
      setSelectedItem(item);
    } else if (mode === 'folder' && item.type === 'folder') {
      setSelectedItem(item);
    }
  };

  const handleDoubleClick = (item) => {
    if (item.type === 'folder') {
      handleFolderClick(item);
    } else if (mode === 'template' && item.type === 'document') {
      onSelect(item);
      onOpenChange(false);
    }
  };

  const handleConfirm = () => {
    if (mode === 'template' && selectedItem) {
      onSelect(selectedItem);
      onOpenChange(false);
    } else if (mode === 'folder') {
      const folderToSelect = selectedItem || (currentFolderId ? { id: currentFolderId, name: breadcrumbs[breadcrumbs.length - 1]?.name } : null);
      if (folderToSelect) {
        onSelect(folderToSelect);
        onOpenChange(false);
      } else {
        toast.error('Seleziona una cartella');
      }
    }
  };

  const handleSelectCurrentFolder = () => {
    if (mode === 'folder' && currentFolderId) {
      const name = breadcrumbs[breadcrumbs.length - 1]?.name || 'Cartella selezionata';
      onSelect({ id: currentFolderId, name: name });
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-blue-600" />
            {title || (mode === 'template' ? 'Seleziona Documento' : 'Seleziona Cartella')}
          </DialogTitle>
          <DialogDescription>
            {description || (needsDriveSelection 
              ? 'Seleziona un Drive condiviso'
              : (mode === 'template' 
                ? 'Naviga nei Drive condivisi e seleziona un documento Google Docs' 
                : 'Naviga e seleziona una cartella di destinazione')
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Shared Drives Selection */}
        {needsDriveSelection && !loading && (
          <div className="flex-1 overflow-auto py-4">
            <p className="text-sm text-slate-500 mb-3">Drive condivisi disponibili:</p>
            {sharedDrives.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <HardDrive className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nessun Drive condiviso trovato</p>
                <p className="text-sm mt-1">Assicurati di avere accesso a dei Drive condivisi</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sharedDrives.map((drive) => (
                  <div
                    key={drive.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => handleSelectSharedDrive(drive)}
                    data-testid={`shared-drive-${drive.id}`}
                  >
                    <HardDrive className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">{drive.name}</span>
                    <ChevronRight className="w-4 h-4 ml-auto text-slate-400" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* File Browser */}
        {!needsDriveSelection && (
          <>
            {/* Search and Breadcrumbs */}
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Cerca..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1"
                />
                <Button variant="outline" size="icon" onClick={handleSearch}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Breadcrumbs */}
              <div className="flex items-center gap-1 text-sm overflow-x-auto pb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 flex-shrink-0"
                  onClick={() => {
                    setNeedsDriveSelection(true);
                    setSelectedDriveId(null);
                    setBreadcrumbs([]);
                    setItems([]);
                  }}
                >
                  <HardDrive className="w-4 h-4" />
                </Button>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={crumb.id || index}>
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 px-2 truncate max-w-[150px] flex-shrink-0 ${
                        index === breadcrumbs.length - 1 ? 'font-medium text-slate-900' : 'text-slate-600'
                      }`}
                      onClick={() => handleBreadcrumbClick(index)}
                    >
                      {crumb.name}
                    </Button>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-auto border rounded-lg min-h-[300px]">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : error === 'drive_not_connected' ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <HardDrive className="w-12 h-12 mb-3 text-slate-300" />
                  <p>Google Drive non connesso</p>
                  <p className="text-sm">Vai nelle Impostazioni per connetterlo</p>
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <FolderOpen className="w-12 h-12 mb-3 text-slate-300" />
                  <p>Cartella vuota</p>
                </div>
              ) : (
                <div className="divide-y">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer transition-colors ${
                        selectedItem?.id === item.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                      }`}
                      onClick={() => handleItemSelect(item)}
                      onDoubleClick={() => handleDoubleClick(item)}
                    >
                      {item.type === 'folder' ? (
                        <FolderOpen className="w-5 h-5 text-amber-500 flex-shrink-0" />
                      ) : (
                        <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      )}
                      <span className="flex-1 truncate">{item.name}</span>
                      {selectedItem?.id === item.id && (
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      )}
                      {item.type === 'folder' && (
                        <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <DialogFooter className="gap-2 sm:gap-0">
          {!needsDriveSelection && breadcrumbs.length > 0 && (
            <Button variant="outline" onClick={handleGoBack} className="mr-auto">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Indietro
            </Button>
          )}
          
          {mode === 'folder' && currentFolderId && !needsDriveSelection && (
            <Button variant="outline" onClick={handleSelectCurrentFolder}>
              Seleziona questa cartella
            </Button>
          )}
          
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          
          {!needsDriveSelection && (
            <Button
              onClick={handleConfirm}
              disabled={mode === 'template' ? !selectedItem : false}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Check className="w-4 h-4 mr-2" />
              Conferma
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DriveBrowser;
